import { initExtension } from '@flywheel-io/extension';
import {
  Fade,
  LinearProgress,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { debounce } from 'lodash';
import React from 'react';

import './App.css';
import Builder from './Builder';
import { CreateNewFormDialog } from './CreateNewFormDialog';
import { exportCsv } from './ExportProject';
import Footer from './Footer';
import FormEditor from './FormEditor';
import FormViewer from './FormViewer';
import Header from './Header';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2e72f6',
      contrastText: '#fff',
    },
    secondary: {
      main: '#fff',
      contrastText: '#2e72f6',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '"Helvetica Neue"',
      'Helvetica',
      'arial',
      'sans-serif',
    ].join(','),
  },
});

const DEFAULT_FORM_DEF = {
  display: 'form',
};

const DEFAULT_SUBMISSON = { data: {} };

const DEFAULT_STATE = {
  builderData: [],
  extension: null,
  error: null,
  exportEnabled: true,
  loading: true,
  metadataDialog: {
    open: false,
    data: null,
    mode: 'create',
  },
};

const mockExtension = {
  params: { mode: 'builder' },
  container: { _id: '5ff5e75110577f8409e45b3d', container_type: 'project' },
  getContainer: () =>
    Promise.resolve({ _id: '5ff5e75110577f8409e45b3d', name: 'MOCK' }),
  channel: { messages$: { subscribe: () => {} } },
  readSetting: () => Promise.resolve({}),
  writeSetting: (projectId, key, value) => Promise.resolve(),
};

export const getProject = async (extension) => {
  const { container } = extension;

  return await extension.getContainer(
    'project',
    container?.container_type === 'project'
      ? container._id
      : container?.parents?.project
  );
};

export const getForms = async (extension, id) => {
  return (await extension.readSetting(id, 'forms')) || {};
};

const getForm = (forms, containerType, key) => {
  return forms?.[containerType]?.find((v) => v.key === key) || DEFAULT_FORM_DEF;
};

const setSize = (extension, maxViewport, contentSize) => {
  const mode = extension?.params?.mode || 'viewer';

  let viewport;

  if (contentSize) {
    // adjust size to include header, footer and padding
    contentSize.height += 145;
    contentSize.width += 30;

    if (contentSize.height > maxViewport.height) {
      viewport = maxViewport;
    } else {
      viewport = contentSize;
    }
  } else {
    viewport = maxViewport;
  }

  if (mode === 'viewer') {
    extension.updateViewport(viewport);
  }
};

class App extends React.Component {
  state = DEFAULT_STATE;

  componentDidMount() {
    let init = initExtension;
    const maxViewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    this.setState({ maxViewport });
    if (window.self === window.top) {
      init = () => Promise.resolve(mockExtension);
    }

    init({ scope: 'ReadWrite', validateOrigin: () => true }).then(
      (extension) => {
        this.setState({ extension }, () => {
          const formId = extension?.params?.formId;
          if (formId) {
            this.handleSelectForm({
              containerType: extension.container?.container_type,
              key: formId,
            });
          }

          extension.channel.messages$.subscribe((message) => {
            if (message.method === 'UPDATE_VIEWPORT') {
              maxViewport.width = message.params.width;
              maxViewport.height = message.params.height;
              setSize(extension, this.state.maxViewport);
            }
          });

          this.loadTable();
        });
      }
    );
  }

  handleCloseMetadataDialog = async (selectedForm) => {
    this.setState({
      metadataDialog: DEFAULT_STATE.metadataDialog,
    });

    if (selectedForm) {
      await this.handleSelectForm(selectedForm);
    }

    this.loadTable();
  };

  handleSaveMetadataDialog = async ({ container, form, key }) => {
    try {
      await this.saveForm(container, form, key);
      if (this.state?.selectedForm) {
        const selectedForm = {
          containerType: container,
          name: form.name,
          key,
          form,
        };
        this.setState({
          selectedForm,
          schema: form,
        });
      }
    } catch (error) {
      this.handleError(error);
    }
  };

  handleError = (error) => {
    this.setState({
      error: error.toString(),
    });
  };

  handleSelectForm = async (formInfo) => {
    try {
      const { containerType, key } = formInfo;
      const project = await getProject(this.state.extension);
      const forms = await getForms(this.state.extension, project._id);
      const form = getForm(forms, containerType, key);
      const submission =
        this.state.extension?.container?.info?.[key] || DEFAULT_SUBMISSON;
      const selectedForm = {
        containerType,
        name: form.name,
        key,
        form,
        submission,
      };
      this.setState(
        { selectedForm, schema: form },
        setSize(this.state.extension, this.state.maxViewport)
      );
    } catch (error) {
      this.handleError(error);
    }
  };

  handleRowClick = async (tableMeta) => {
    if (this.state.extension?.params?.mode === 'builder') {
      await this.handleFormEdit(tableMeta);
    } else {
      await this.handleFormViewer(tableMeta);
    }
  };

  handleFormViewer = async (tableMeta) => {
    try {
      const { containerType, name, key } = tableMeta.rowData;
      const project = await getProject(this.state.extension);
      const forms = await getForms(this.state.extension, project._id);
      const form = getForm(forms, containerType, key);
      const submission =
        this.state.extension?.container?.info?.[key] || DEFAULT_SUBMISSON;
      const selectedForm = {
        containerType,
        name,
        key,
        form,
        submission,
      };
      this.setState({ selectedForm, schema: form });
    } catch (error) {
      this.handleError(error);
    }
  };

  handleFormEdit = async (tableMeta) => {
    try {
      const { containerType, name, key } = tableMeta.rowData;
      const project = await getProject(this.state.extension);
      const forms = await getForms(this.state.extension, project._id);
      const form = getForm(forms, containerType, key);
      const submission =
        this.state.extension?.container?.info?.[key] || DEFAULT_SUBMISSON;
      const selectedForm = {
        containerType,
        name,
        key,
        form,
        submission,
      };
      this.setState({ selectedForm, schema: form });
    } catch (error) {
      this.handleError(error);
    }
  };

  handleFormEditDetails = async (tableMeta) => {
    try {
      // eslint-disable-next-line no-unused-vars
      const [containerType, name, key] = tableMeta.rowData;
      const project = await getProject(this.state.extension);
      const forms = await getForms(this.state.extension, project._id);
      const form = getForm(forms, containerType, key);
      const formInfo = {
        containerType,
        key,
        form,
      };
      this.handleMetadataDialogClick('edit', formInfo);
    } catch (error) {
      this.handleError(error);
    }
  };

  handleMetadataDialogClick = (mode, formInfo) => {
    this.setState({
      metadataDialog: {
        open: true,
        data: formInfo,
        mode: mode,
      },
    });
  };

  handleExportForm = async (type) => {
    try {
      const csv = await exportCsv({
        extension: this.state.extension,
        exportType: type,
      });
      this.download('Flywheel.csv', csv);
    } catch (error) {
      this.handleError(error);
    }
  };

  handleDuplicateForm = async (tableMeta) => {
    try {
      const [containerType, name, key] = tableMeta.rowData;
      const project = await getProject(this.state.extension);
      const forms = await getForms(this.state.extension, project._id);
      const newKey = `${key}#${Date.now()}`;
      const form = Object.assign({}, getForm(forms, containerType, key), {
        key: newKey,
      });
      const selectedForm = {
        containerType,
        name,
        key: newKey,
        form,
        submission: {},
      };

      await this.saveForm(containerType, form, newKey);
      this.setState({ selectedForm, schema: form });
    } catch (error) {
      this.handleError(error);
    }
  };

  handleDeleteForm = async (tableMeta) => {
    try {
      const extension = this.state.extension;
      // eslint-disable-next-line no-unused-vars
      const [containerType, name, key] = tableMeta.rowData;
      const project = await getProject(extension);
      let allForms = await getForms(extension, project._id);
      const forms = Object.assign({}, allForms);
      forms[containerType] = allForms[containerType].filter(
        (v) => v.key !== key
      );
      extension.writeSetting(project._id, 'forms', forms).then(() => {
        this.setState({ selectedForm: null });
        this.loadTable();
      });
    } catch (error) {
      this.handleError(error);
    }
  };

  saveForm = async (containerType, form, key) => {
    try {
      const extension = this.state.extension;
      const project = await getProject(extension);
      const existingForms = await getForms(extension, project._id);
      let forms = Object.assign({}, existingForms);
      const index = (forms[containerType] || []).findIndex(
        (v) => v.key === key
      );
      if (index > -1) {
        forms[containerType][index] = form;
      } else {
        forms[containerType] = [...(forms[containerType] || []), form];
      }

      return await extension.writeSetting(project._id, 'forms', forms);
    } catch (error) {
      this.handleError(error);
    }
  };

  /*
   * handleSaveForm - used to save the form definition
   */
  handleSaveForm = async () => {
    try {
      const { containerType } = this.state.selectedForm;
      const { key } = this.state.schema;
      const form = Object.assign({}, this.state.schema);
      await this.saveForm(containerType, form, key);
      this.setState({ selectedForm: null });
      this.loadTable();
    } catch (error) {
      this.handleError(error);
    }
  };

  handleChangeSchema = (schema) => {
    this.setState({ schema });
  };

  handleClickBack = () => {
    const mode = this.state.extension?.params?.mode || 'viewer';

    if (this.state.selectedForm) {
      this.setState({ selectedForm: null });
    }

    if (mode === 'viewer') {
      setSize(this.state.extension, this.state.maxViewport);
    }
  };

  handleEditFormMetadata = () => {
    const formInfo = {
      containerType: this.state.selectedForm.containerType,
      key: this.state.selectedForm.key,
      form: this.state.selectedForm.form,
    };
    this.handleMetadataDialogClick('edit', formInfo);
  };

  close = () => {
    if (this.state?.extension) {
      this.state.extension.close();
    }
  };

  /*
   * handleSubmitForm - form submissions (user responses)
   */

  handleSubmitForm = debounce((submission) => {
    const extension = this.state.extension;
    const formData = {
      ...submission,
      metadata: {
        origin: {
          id: extension.user._id,
          type: 'user',
        },
        date: Date.now(),
      },
    };

    extension
      .updateContainerInfo(extension?.container, {
        set: {
          [this.state.selectedForm.key]: formData,
        },
      })
      .then(() => {
        extension.close();
      });
  }, 250);

  download = (name, data) => {
    const link = document.createElement('a');
    const blob = new Blob([data], { type: 'octet/stream' });
    const url = window.URL.createObjectURL(blob);
    link.setAttribute('download', name);
    link.href = url;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  loadTable = async () => {
    this.setState({ loading: true });

    try {
      const mode = this.state.extension?.params?.mode || 'viewer';
      const activeContainerType = this.state.extension.container?.container_type;
      const project = await getProject(this.state.extension);
      const data = [];
      let filteredData = [];
      const forms = await getForms(this.state.extension, project._id);
      for (const [containerType, containerData] of Object.entries(forms)) {
        // eslint-disable-next-line no-unused-vars
        for (const [key, formDef] of Object.entries(containerData)) {
          data.push({
            containerType,
            name: formDef?.name || '-',
            key: formDef.key,
            fields: formDef?.components?.length || 0,
            description: formDef?.description || '-',
            formDef: formDef,
          });
        }
      }

      if (mode === 'viewer') {
        filteredData = data.filter(
          (form) => form.containerType === activeContainerType
        );
      } else {
        filteredData = data;
      }

      this.setState({
        builderData: filteredData,
        loading: false,
      });
    } catch (error) {
      this.setState({
        error: error.toString(),
        loading: false,
      });
    }
  };

  render() {
    if (!this.state.extension) {
      return (
        <ThemeProvider theme={theme}>
          <Fade
            in={this.state.loading}
            style={{
              transitionDelay: this.state.loading ? '800ms' : '0ms',
            }}
            unmountOnExit
          >
            <LinearProgress
              style={{
                width: '100vw',
              }}
            />
          </Fade>
        </ThemeProvider>
      );
    }

    const mode = this.state.extension?.params?.mode || 'viewer';
    const title = mode === 'builder' ? 'Form Builder' : 'Form Viewer';

    return (
      <ThemeProvider theme={theme}>
        <div className="app">
          <Header
            title={title}
            mode={mode}
            selectedForm={this.state.selectedForm}
            onClickBack={this.handleClickBack}
            onEditFormMetadata={this.handleEditFormMetadata}
          ></Header>
          <div id="main" className="main">
            <Fade
              in={this.state.loading}
              style={{
                transitionDelay: this.state.loading ? '0ms' : '0ms',
                width: '100vw',
                flex: 'none',
              }}
              unmountOnExit
            >
              <LinearProgress />
            </Fade>
            {this.state.error && (
              <div className="error">
                <div className="error-icon circle-icon">
                  <ErrorIcon />
                </div>
                <div className="primary">
                  An unexpected error occurred. Please try again.
                </div>
                <div>{this.state.error}</div>
              </div>
            )}
            {!this.state.error &&
              this.state.selectedForm &&
              mode === 'builder' && (
                <FormEditor
                  selectedForm={this.state.selectedForm}
                  schema={this.state.schema}
                  onChangeSchema={this.handleChangeSchema}
                ></FormEditor>
              )}
            {!this.state.error &&
              this.state.selectedForm &&
              mode === 'viewer' && (
                <FormViewer
                  form={this.state.selectedForm.form}
                  submission={this.state.selectedForm.submission}
                  onSubmit={this.handleSubmitForm}
                  setSize={setSize.bind(
                    this,
                    this.state.extension,
                    this.state.maxViewport
                  )}
                ></FormViewer>
              )}
            {!this.state.error && !this.state.selectedForm && (
              <Builder
                forwardRef={(ref) => {
                  this.builder = ref;
                }}
                data={this.state.builderData}
                exportEnabled={this.state.exportEnabled}
                mode={mode}
                onRowClick={this.handleRowClick}
                onEditFormDetails={this.handleFormEditDetails}
                onExportForm={this.handleExportForm}
                onDuplicateForm={this.handleDuplicateForm}
                onDeleteForm={this.handleDeleteForm}
              ></Builder>
            )}

            <CreateNewFormDialog
              open={this.state.metadataDialog.open}
              data={this.state.metadataDialog.data}
              onClose={this.handleCloseMetadataDialog}
              onSave={this.handleSaveMetadataDialog}
              mode={this.state.metadataDialog.mode}
            />
          </div>
          <Footer
            extension={this.state.extension}
            selectedForm={this.state.selectedForm}
            onSaveForm={this.handleSaveForm}
            onSubmitForm={this.handleSubmitForm}
            onCreateNewForm={this.handleMetadataDialogClick.bind(
              this,
              'create',
              {}
            )}
          ></Footer>
        </div>
      </ThemeProvider>
    );
  }
}

export default App;
